import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import styles from './agwa-state-badge.styles.css?inline';
import {
  resolveByLabel,
  resolveState,
  type StateDomainKey,
} from '../../utility/state-colours/state-colour-registry';
import { subscribeStateColourOverrides } from '../../utility/state-colours/state-colour-overrides-store';
import { ensureStateColourOverridesLoaded } from '../../utility/state-colours/state-colour-overrides-loader';

/**
 * The one way a workflow state is rendered as a badge, suite-wide.
 *
 * Replaces per-module variant mappers and hard-coded `<sl-badge variant=...>` literals (D-040):
 * the colour comes from the central state-colour registry, the word comes from the label entry,
 * and a value the registry does not know renders the deliberately neutral UNMAPPED look instead
 * of borrowing a colour that claims a meaning.
 *
 * Two resolution modes, chosen by whether `domain` is set:
 * - WITH a domain: the value is one of that enum's states, matched case-insensitively (the wire
 *   sends camelCase, the generated model says PascalCase - see the registry).
 * - WITHOUT a domain: the value is a bare display string (the search dialog's status cells get
 *   whatever text the search handler sent) and resolves by label word instead.
 *
 * `face` exists for the Badge Colours settings page, which shows every badge in BOTH themes on
 * one screen: 'light' and 'dark' pin the corresponding authored face of the colour regardless of
 * the viewer's theme. Everything else uses the default 'auto', which follows the theme through
 * the live -fill tokens.
 *
 * Portability: props in, one span out. No router, no fetch, no host assumptions.
 */
@customElement('agwa-state-badge')
export class AgwaStateBadge extends LitElement {
  static styles = unsafeCSS(styles);

  /** Which state enum `state` belongs to. Omit for bare display strings (label resolution). */
  @property() domain?: StateDomainKey;

  /** The state value: an enum value in any casing, or a display string when domain is unset. */
  @property() state?: string;

  /** 'auto' follows the theme; 'light' / 'dark' pin an authored face for theme previews. */
  @property() face: 'auto' | 'light' | 'dark' = 'auto';

  private unsubscribeOverrides?: () => void;

  connectedCallback(): void {
    super.connectedCallback();
    // Every badge on screen repaints when an admin's override lands (their own save, or the
    // SSE nudge from another session). The first badge anywhere triggers the one fetch.
    ensureStateColourOverridesLoaded();
    this.unsubscribeOverrides = subscribeStateColourOverrides(() =>
      this.requestUpdate()
    );
  }

  disconnectedCallback(): void {
    this.unsubscribeOverrides?.();
    this.unsubscribeOverrides = undefined;
    super.disconnectedCallback();
  }

  render() {
    const resolved = this.domain
      ? resolveState(this.domain, this.state)
      : resolveByLabel(this.state);
    if (resolved.colour === 'unmapped') {
      return html`<span class="badge unmapped" role="status"
        >${resolved.label}</span
      >`;
    }
    // The fill is an inline REFERENCE to the palette token for the resolved colour and face -
    // never a value. Colour values live only in tokens.css; the node spec beside the registry
    // guarantees every palette key has its token, so this reference cannot dangle. A reference
    // rather than per-colour CSS classes because the gamut grows (architect, 2026-09-04) and a
    // class per colour per face is a maintenance sweep on every addition.
    const suffix = this.face === 'auto' ? 'fill' : this.face;
    return html`<span
      class="badge"
      role="status"
      style="--badge-fill: var(--agwa-state-${resolved.colour}-${suffix})"
      >${resolved.label}</span
    >`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-state-badge': AgwaStateBadge;
  }
}
